import { useState } from 'react'
import type { ConstitutionEditSuggestion as ConstitutionEditSuggestionType } from '@/types'

const API_BASE = 'http://localhost:5001/api/portfolio-manager'

interface ConstitutionEditSuggestionCardProps {
  suggestion: ConstitutionEditSuggestionType
  onApproved: () => void
  onRejected: () => void
}

export function ConstitutionEditSuggestionCard({
  suggestion,
  onApproved,
  onRejected,
}: ConstitutionEditSuggestionCardProps) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleApprove = async () => {
    setLoading(true)
    setError(null)
    try {
      // Fetch current constitution
      const getRes = await fetch(`${API_BASE}/constitution`)
      if (!getRes.ok) {
        throw new Error(`Failed to fetch constitution (${getRes.status})`)
      }
      const constitution = await getRes.json()

      // Replace the target section with proposed content
      const updatedContent = replaceSectionContent(
        constitution.content || '',
        suggestion.targetSection,
        suggestion.proposedContent
      )

      // Save updated constitution
      const putRes = await fetch(`${API_BASE}/constitution`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          content: updatedContent,
          changeSource: 'ai_suggestion',
        }),
      })

      if (!putRes.ok) {
        const errData = await putRes.json().catch(() => ({}))
        throw new Error(errData.error || `Failed to update constitution (${putRes.status})`)
      }

      onApproved()
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="mt-3 rounded-lg border border-[var(--color-accent)]/30 bg-[var(--color-bg-tertiary)] overflow-hidden">
      {/* Header */}
      <div className="px-4 py-2 border-b border-[var(--color-border)] bg-[var(--color-accent)]/5">
        <span className="text-xs font-semibold text-[var(--color-accent)]">
          📝 Suggested Constitution Edit
        </span>
      </div>

      {/* Body */}
      <div className="px-4 py-3 space-y-3">
        {/* Target section */}
        <div>
          <span className="text-xs text-[var(--color-text-muted)]">Section: </span>
          <span className="text-xs font-medium text-[var(--color-text-primary)]">
            {suggestion.targetSection}
          </span>
        </div>

        {/* Diff display */}
        <div className="rounded border border-[var(--color-border)] bg-[var(--color-bg-primary)] p-3 font-mono text-xs overflow-x-auto">
          {suggestion.proposedContent.split('\n').map((line, idx) => {
            let lineClass = 'text-[var(--color-text-primary)]'
            if (line.startsWith('+')) {
              lineClass = 'text-green-400'
            } else if (line.startsWith('-')) {
              lineClass = 'text-red-400'
            }
            return (
              <div key={idx} className={lineClass}>
                {line || '\u00A0'}
              </div>
            )
          })}
        </div>

        {/* Rationale */}
        <div>
          <span className="text-xs text-[var(--color-text-muted)]">Rationale: </span>
          <span className="text-xs text-[var(--color-text-primary)]">
            {suggestion.rationale}
          </span>
        </div>

        {/* Error */}
        {error && (
          <div className="text-xs text-red-400 bg-red-400/10 border border-red-400/30 rounded px-3 py-1.5">
            ⚠️ {error}
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-2 pt-1">
          <button
            onClick={handleApprove}
            disabled={loading}
            className="px-3 py-1.5 text-xs rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 transition-colors"
          >
            {loading ? '...' : '✓ Approve'}
          </button>
          <button
            onClick={onRejected}
            disabled={loading}
            className="px-3 py-1.5 text-xs rounded border border-[var(--color-border)] text-[var(--color-text-muted)] hover:border-red-400 hover:text-red-400 disabled:opacity-50 transition-colors"
          >
            ✗ Reject
          </button>
        </div>
      </div>
    </div>
  )
}

/**
 * Replace a section's content in a markdown document.
 * Sections are delimited by ## headings.
 */
function replaceSectionContent(
  fullContent: string,
  targetSection: string,
  proposedContent: string
): string {
  const lines = fullContent.split('\n')
  const sectionHeadingPattern = new RegExp(
    `^##\\s+${escapeRegex(targetSection)}\\s*$`,
    'i'
  )

  let sectionStart = -1
  let sectionEnd = lines.length

  for (let i = 0; i < lines.length; i++) {
    if (sectionHeadingPattern.test(lines[i])) {
      sectionStart = i
      // Find the end of this section (next ## heading or end of file)
      for (let j = i + 1; j < lines.length; j++) {
        if (/^##\s+/.test(lines[j])) {
          sectionEnd = j
          break
        }
      }
      break
    }
  }

  if (sectionStart === -1) {
    // Section not found — append at end
    return fullContent + `\n\n## ${targetSection}\n${proposedContent}\n`
  }

  // Replace section content (keep heading, replace body)
  const before = lines.slice(0, sectionStart + 1)
  const after = lines.slice(sectionEnd)

  return [...before, proposedContent, '', ...after].join('\n')
}

function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}
